/**
 * The Tagebuch stores a day's temperature as the bare reading — "+10", "-20" —
 * and every place that shows it puts the unit back on: the form, the grid, the
 * PDF and the ExcaBook sheet.
 *
 * Both directions are one contract: what the field sheets print ("+35°C") has
 * to read back as "+35", and what we write out has to say "°C" exactly once.
 */

/** A reading we can put a unit on: an optional sign, digits, one decimal. */
const READING_SOURCE = String.raw`[+-]?\d{1,3}(?:[.,]\d+)?`;

/**
 * A SPAN of two readings — the coldest and the warmest hour of the day, which is
 * how a good part of the crew fills the field: "8-22", "-4 - 0", "13 bis 22".
 */
const SPAN_SOURCE = String.raw`${READING_SOURCE}\s*(?:-|–|—|bis)\s*${READING_SOURCE}`;

/**
 * The words a reading gets qualified with — "max. +8" is a temperature. A
 * CLOSED list on purpose: accepting any prefix would unit everything that
 * happens to end in a number, and "Regen ab 14" means two o'clock.
 */
const QUALIFIER_SOURCE = String.raw`(?:max|min|ca|etwa|ungefähr)\.?\s+`;

/**
 * Anything we can put a unit on: a reading or a span, either optionally
 * qualified. Written out with whatever separator and spacing it was typed with
 * — the value is the crew's wording of their day and not ours to tidy.
 */
const MEASURED = new RegExp(
    `^(?:${QUALIFIER_SOURCE})?(?:${SPAN_SOURCE}|${READING_SOURCE})$`,
    'i'
);

/** "°C", " ° c", "°" — the unit as the sheets and the tools spell it. */
const UNIT = /\s*°\s*c?/gi;

/**
 * The parts of a value that names more than one reading. Split on ", " and not
 * on "," alone: the decimal separator here is a German comma, so "19,5" is ONE
 * reading.
 */
function parts(value: string): string[] {
    return value
        .split(/,\s+/)
        .map((part) => part.trim())
        .filter((part) => part !== '');
}

/**
 * '+35°C' → '+35'. Free text the field also accepts ("sehr heiß") passes
 * through; only the unit goes.
 */
export function stripTemperatureUnit(value: string): string {
    return parts(value.replace(UNIT, '')).join(', ');
}

/**
 * '+35' → '+35 °C', for anything that displays or exports the value. A stored
 * value that still carries its unit is normalized rather than doubled, and a
 * value that is not a reading is left as the words it is. A SPAN takes ONE
 * unit, at the end: "8-22 °C", not "8 °C-22 °C". Same for "max. +8 °C".
 */
export function formatTemperature(value: string): string {
    return parts(stripTemperatureUnit(value))
        .map((part) => (MEASURED.test(part) ? `${part} °C` : part))
        .join(', ');
}
